import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useCustomisation } from "../../../../../context/CustomisationContext";
import useScreenSize from "../../../../../hooks/useScreenSize";
import {
  AssignmentType,
  ImportanceType,
  ProgressStatus,
} from "../../../../../services/api/types/general/homeAssignment";
import { brand, gray } from "../../../../../theme/themePrimitives";
import { getShadeColour } from "../../../../../utils/helpers";

export type FilterType = {
  title: string;
  filterKey?: string;
  items: {
    id: string;
    name: string;
    checked: boolean;
    value?: string | ProgressStatus | AssignmentType | ImportanceType;
    isAll?: boolean;
  }[];
};

interface AssignmentsFilterProps {
  showFilter: boolean;
  setShowFilter: React.Dispatch<React.SetStateAction<boolean>>;
  filtersList: FilterType[];
  handleCheckboxChange: (index: number, itemIndex: number) => void;
  clearFilters: () => void;
  saveFilters: () => void;
  disableClearAll?: boolean;
  loadingGroups?: boolean;
  isPreviewMode?: boolean;
  onClose?: () => void;
  filtersTitle?: string;
  saveLabel?: string;
  clearAllLabel?: string;
}

export const AssignmentsFilter: React.FC<AssignmentsFilterProps> = ({
  showFilter,
  setShowFilter,
  filtersList,
  handleCheckboxChange,
  clearFilters,
  saveFilters,
  disableClearAll,
  loadingGroups = false,
  isPreviewMode = false,
  onClose,
  filtersTitle = "Filters",
  saveLabel = "Save",
  clearAllLabel = "Clear all",
}) => {
  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      setShowFilter(false);
    }
  };
  const [hoveredCheckbox, setHoveredCheckbox] = useState<string | null>(null);
  const { isMobile } = useScreenSize();
  const { customisations } = useCustomisation();
  const {
    GPC: { buttonsSettings: gpcbuttonssettings },
    HOME: { buttonsSettings: homeButtonsSettings },
  } = customisations.customisations;
  const [hasCheckedAny, setHasCheckedAny] = useState(false);
  const isAssignmentSearch =
    window.location.pathname.includes("/assignment-search");
  const isHome = window.location.pathname.startsWith("/home");

  const primaryButtonColor = isAssignmentSearch
    ? brand[600]
    : isHome
      ? homeButtonsSettings?.homeBtnPrimaryBgColor
      : gpcbuttonssettings?.gpcBtnPrimaryBgColor;

  const primaryButtonTextColor = isAssignmentSearch
    ? "white"
    : isHome
      ? homeButtonsSettings?.homeBtnPrimaryTextColor
      : gpcbuttonssettings?.gpcBtnPrimaryTextColor;

  useEffect(() => {
    const anyChecked = filtersList.some((group) =>
      group.items.some((item) => item.checked === true),
    );
    setHasCheckedAny(anyChecked);
  }, [filtersList, disableClearAll]);

  return (
    <Drawer
      id="my-assignments-filter-drawer"
      anchor="right"
      open={showFilter}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: "white",
            boxSizing: "border-box",
            width: isMobile ? "100%" : "350px",
            padding: "1rem",
          },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography sx={{ color: "#000 !important" }} variant="h6">
            {filtersTitle}
          </Typography>
          <IconButton id="my-assignments-filter-close-button" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflowY: "auto", pr: 1 }}>
          {loadingGroups ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <CircularProgress size={60} />
            </Box>
          ) : (
            filtersList
              .map((item, originalIndex) => ({ item, originalIndex }))
              .filter(({ item }) => !isPreviewMode || item.filterKey === "type")
              .map(({ item, originalIndex }) => (
              <Box key={originalIndex} sx={{ mb: 2 }}>
                <Typography color="#000" sx={{ fontWeight: 600, mb: 1 }}>
                  {item.title}
                </Typography>
                {item.items.map((i, itemIndex) => (
                  <Box
                    key={i.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={() => handleCheckboxChange(originalIndex, itemIndex)}
                    onMouseEnter={() => setHoveredCheckbox(i.id)}
                    onMouseLeave={() => setHoveredCheckbox(null)}
                  >
                    <Checkbox
                      checked={i.checked}
                      sx={{
                        ...(hoveredCheckbox === `${i.id}` && {
                          borderColor: gray[400],
                        }),
                      }}
                      id={i.id}
                      name={i.name}
                      data-parent-name={item.title}
                    />
                    <Typography color="#000">{i.name}</Typography>
                  </Box>
                ))}
              </Box>
            ))
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            pt: 2,
            borderTop: `1px solid ${gray[300]}`,
          }}
        >
          <Button
            onClick={saveFilters}
            sx={{
              borderRadius: "12px",
              backgroundColor: `${primaryButtonColor} !important`,
              color: `${primaryButtonTextColor}`,
              fontWeight: 600,
              "&:hover": {
                bgcolor: `${getShadeColour(primaryButtonColor!, -8)} !important`,
                fontWeight: 700,
              },
            }}
          >
            {saveLabel}
          </Button>
          <Button
            variant="outlined"
            onClick={clearFilters}
            sx={{
              borderRadius: "12px",
              backgroundColor: `#fff`,
              border: "1px solid  #000 !important",
              color: "#000",
              fontWeight: 600,
              "&.Mui-disabled": {
                border: "1px solid #4d4d4d42",
                color: "#999",
                backgroundColor: "#fff",
              },
            }}
            disabled={!hasCheckedAny || disableClearAll}
          >
            {clearAllLabel}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
